import { cloneDeep, has, keyBy, map, mapValues, omit } from 'lodash';

export const normalizeName = name => (name ? name.replace(/\./g, '-') : name);

const buildNodeLabelMap = metaModel => {
    const allNodes = [...(metaModel.dimensions || []), ...(metaModel.metrics || [])];
    const nodesWithLabels = allNodes.filter(node => node.name && node.label);
    const nodesByName = keyBy(nodesWithLabels, 'name');

    return new Map(Object.entries(mapValues(nodesByName, 'label')));
};

const enrichNodeReference = (nodeRef, labelMap) => {
    if (!nodeRef || !nodeRef.name) {
        return nodeRef;
    }

    const normalizedName = normalizeName(nodeRef.name);
    const label = nodeRef.label || labelMap.get(normalizedName) || nodeRef.name;

    return {
        ...nodeRef,
        name: normalizedName,
        label,
    };
};

const normalizeReportReference = config => {
    if (!config.reportReference) {
        return config;
    }

    const { reportReference, ...rest } = config;
    return {
        ...rest,
        reportRef: config.reportRef || reportReference,
    };
};

const enrichColumn = (column, enrichNodeRefFn) => ({
    ...column,
    nodeReference: enrichNodeRefFn(column.nodeReference),
});

const normalizeFilterProperties = filter => {
    if (!has(filter, 'filterValues')) {
        return filter;
    }

    return {
        ...omit(filter, 'filterValues'),
        values: filter.values || filter.filterValues,
    };
};

const enrichFilter = (filter, enrichNodeRefFn) => {
    const enriched = {
        ...filter,
        nodeReference: enrichNodeRefFn(filter.nodeReference),
    };

    return normalizeFilterProperties(enriched);
};

/**
 * Transformations performed:
 * - Adds labels to nodeReferences from the metamodel
 * - Normalizes node names (dots to dashes)
 * - Normalizes property names: reportReference -> reportRef, filterValues -> values
 */
export const enrichDynamicReportWithLabels = (reportConfig, metaModel) => {
    if (!reportConfig || !metaModel) {
        return reportConfig;
    }

    const labelMap = buildNodeLabelMap(metaModel);
    const enrichNodeRefFn = nodeRef => enrichNodeReference(nodeRef, labelMap);

    const clonedConfig = cloneDeep(reportConfig);
    const normalizedConfig = normalizeReportReference(clonedConfig);

    const enrichedColumns = normalizedConfig.columns?.values
        ? {
              ...normalizedConfig.columns,
              values: map(normalizedConfig.columns.values, col => enrichColumn(col, enrichNodeRefFn)),
          }
        : normalizedConfig.columns;

    const enrichedFilters = normalizedConfig.filters?.values
        ? {
              ...normalizedConfig.filters,
              values: map(normalizedConfig.filters.values, filter => enrichFilter(filter, enrichNodeRefFn)),
          }
        : normalizedConfig.filters;

    return {
        ...normalizedConfig,
        ...(enrichedColumns && { columns: enrichedColumns }),
        ...(enrichedFilters && { filters: enrichedFilters }),
    };
};
